import React from 'react';
import { trackRegisterDismissReason } from '../utils/analytics';

/**
 * DismissReasonSheet — one question, asked once, when someone declines to
 * register.
 *
 * This is the only mechanism in the product that gets an answer from the exact
 * people we're losing: they saw the value and still said no. Behavioural events
 * tell us WHAT people did; this tells us WHY they didn't — and each answer maps
 * to a different fix ("don't want another account" = the ask is too heavy;
 * "questions about my photos" = a trust gap we can close with a sentence).
 *
 * One tap, no typing, no follow-up. Shown once per device so a thoughtful
 * question never becomes nagging.
 */

const SANS = '"DM Sans", system-ui, sans-serif';
const ASKED_KEY = 'sonmi_asked_dismiss_reason';

export function getHasAskedDismissReason() {
  try {
    return localStorage.getItem(ASKED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setHasAskedDismissReason() {
  try {
    localStorage.setItem(ASKED_KEY, 'true');
  } catch {}
}

const REASONS = [
  { label: 'Just browsing', value: 'just_browsing' },
  { label: "Don't want another account", value: 'another_account' },
  { label: 'I need to see more first', value: 'need_to_see_more' },
  { label: 'Questions about my photos', value: 'photo_questions' },
  { label: 'Something else', value: 'other' },
];

export default function DismissReasonSheet({ isOpen, onClose }) {
  if (!isOpen) return null;

  const pick = (value) => {
    trackRegisterDismissReason(value);
    setHasAskedDismissReason();
    onClose?.();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        fontFamily: SANS,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setHasAskedDismissReason();
          onClose?.();
        }
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '24px 24px 0 0',
          width: '100%',
          maxWidth: 480,
          padding: '12px 24px 32px',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D1C9C0', margin: '0 auto 20px' }} />
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: 0, textAlign: 'center' }}>
          What's stopping you today?
        </h2>
        <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, margin: '8px 0 20px', textAlign: 'center' }}>
          One tap — it genuinely helps me make this better.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, justifyContent: 'center' }}>
          {REASONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => pick(r.value)}
              style={{
                padding: '11px 16px',
                borderRadius: 999,
                border: '1px solid rgba(42,50,75,0.15)',
                background: '#FFFFFF',
                color: '#2A324B',
                fontSize: 13.5,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => { setHasAskedDismissReason(); onClose?.(); }}
          style={{
            width: '100%',
            padding: '12px',
            marginTop: 18,
            background: 'transparent',
            border: 'none',
            color: '#AFA99F',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
